import * as React from 'react';
import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import { toast } from 'react-toastify';
import { ReactComponent as AddSvg } from 'assets/images/icons/add_24px.svg';
import { ReactComponent as CancelSvg } from 'assets/images/icons/close_24px.svg';
import { ReactComponent as SendSvg } from 'assets/images/icons/send.svg';
import { ClipLoader } from 'react-spinners';
import { InputComponent } from 'component/Input';
import { SelectComponent } from 'component/Select';
import { ButtonComponent } from 'component/Button';
import { SwitherComponent } from 'component/Swither';
import { getAllUsersForTokens } from 'api/UserAPI';
import { UserLabel } from 'pages/Profile/Notifications/Table';
import {
  createOrganization,
  updateOrganization,
  getOrganizationById,
} from 'api/OrganizationsApi';

const REPORT_TYPES = ['nts', 'ha', 'eore'];

const LAYER_LABELS = {
  nts: 'NTS',
  ha: 'HA',
  eore: 'EORE',
};

const emptyLayer = () => ({ validated_application_id: '', not_validated_application_id: '' });

const buildLayerState = (arcgisLayers = []) => {
  const state = { nts: emptyLayer(), ha: emptyLayer(), eore: emptyLayer() };
  arcgisLayers.forEach((layer) => {
    if (state[layer.report_type] !== undefined) {
      state[layer.report_type] = {
        validated_application_id: layer.validated_application_id || '',
        not_validated_application_id: layer.not_validated_application_id || '',
      };
    }
  });
  return state;
};

export const OrganizationDialog = ({ mode, orgId, onSuccess, triggerLabel, triggerIcon }) => {
  const [open, setOpen] = useState(false);
  const [loader, setLoader] = useState(false);

  const [fullNameUk, setFullNameUk] = useState('');
  const [name, setName] = useState('');
  const [abbreviation, setAbbreviation] = useState('');
  const [imsmaUsername, setImsmaUsername] = useState('');
  const [imsmaPassword, setImsmaPassword] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [layers, setLayers] = useState({ nts: emptyLayer(), ha: emptyLayer(), eore: emptyLayer() });
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isCreate = mode === 'create';

  const convertObjectToArrayUsers = (value) => {
    if (!value) return [];
    const list = Array.isArray(value) ? value : Object.values(value);

    return list.map((user) => {
      const transformUserToLabel = {
        email: user.email,
        id: user.id,
        name: user.name,
        surname: user.surname,
        short_role_name: user.role?.short_name,
        role_color: user.role?.color,
      };

      return {
        value: user.id,
        label: <UserLabel user={transformUserToLabel} />,
      };
    });
  };

  const resetForm = () => {
    setFullNameUk('');
    setName('');
    setAbbreviation('');
    setImsmaUsername('');
    setImsmaPassword('');
    setIsActive(true);
    setLayers({ nts: emptyLayer(), ha: emptyLayer(), eore: emptyLayer() });
    setSelectedUsers([]);
  };

  const loadOrganization = () => {
    if (!orgId) return;
    setLoader(true);
    Promise.all([
      getOrganizationById(orgId),
      getAllUsersForTokens(),
    ])
      .then(([orgRes, usersRes]) => {
        const org = orgRes.data;
        const usersData = usersRes.data;

        setAllUsers(usersData);
        setFullNameUk(org.full_name_uk || '');
        setName(org.name || '');
        setAbbreviation(org.abbreviation || '');
        setImsmaUsername(org.imsma_username || '');
        setImsmaPassword('');
        setIsActive(org.is_active !== undefined ? org.is_active : true);
        setLayers(buildLayerState(org.arcgis_layers || []));

        const orgMembers = org.users_detail ?? org.users;
        if (orgMembers?.length) {
          const userOptions = convertObjectToArrayUsers(usersData);
          const orgUserIds = orgMembers
            .map((user) => {
              if (typeof user === 'number') return user;
              if (typeof user === 'string') return Number(user) || user;
              if (typeof user === 'object' && user !== null) {
                return user.id ?? user.user ?? user.user_id ?? null;
              }
              return null;
            })
            .filter((id) => id !== null && id !== undefined);

          setSelectedUsers(userOptions.filter((user) => orgUserIds.includes(user.value)));
        }
      })
      .catch(() => toast.error('Failed to load organization details'))
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    if (isCreate) {
      getAllUsersForTokens().then((res) => setAllUsers(res.data));
    }
  }, []);

  const handleOpen = () => {
    if (!isCreate) {
      loadOrganization();
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    if (isCreate) resetForm();
  };

  const updateLayer = (type, field, value) => {
    setLayers((prev) => ({
      ...prev,
      [type]: { ...prev[type], [field]: value },
    }));
  };

  const buildArcgisLayers = () => REPORT_TYPES.map((type) => ({
    report_type: type,
    validated_application_id: layers[type].validated_application_id,
    not_validated_application_id: layers[type].not_validated_application_id,
  }));

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Short name is required');
      return;
    }
    if (!abbreviation.trim()) {
      toast.error('IMSMA abbreviation is required');
      return;
    }

    setLoader(true);
    const payload = {
      full_name_uk: fullNameUk,
      name: name.trim(),
      abbreviation: abbreviation.trim(),
      imsma_username: imsmaUsername,
      is_active: isActive,
      arcgis_layers: buildArcgisLayers(),
      user_ids: selectedUsers.map((user) => user.value),
    };

    if (imsmaPassword) {
      payload.imsma_password = imsmaPassword;
    }

    try {
      if (isCreate) {
        await createOrganization(payload);
        toast.success('Organization created');
      } else if (isEdit) {
        await updateOrganization(orgId, payload);
        toast.success('Organization updated');
      }
      handleClose();
      onSuccess?.();
    } catch (err) {
      const msg = err?.response?.data?.detail
        || err?.response?.data?.non_field_errors?.[0]
        || 'Error saving organization';
      toast.error(msg);
    } finally {
      setLoader(false);
    }
  };

  const dialogTitle = isCreate ? 'Create organization' : isEdit ? 'Edit organization' : 'Organization details';

  return (
    <div className={isCreate ? 'text-center' : ''}>
      {isCreate ? (
        <ButtonComponent
          iconLeft={<AddSvg />}
          label={triggerLabel || 'Add organization'}
          handleButton={handleOpen}
        />
      ) : (
        <button
          type="button"
          className="icon-action-btn"
          onClick={handleOpen}
          title={dialogTitle}
        >
          {triggerIcon}
        </button>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="org-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <div className="confirm-token-popup org-dialog">
          <div className="header">
            <span className="title">{dialogTitle}</span>
            <CancelSvg className="closeBtn" onClick={handleClose} />
          </div>

          {loader && !isView ? (
            <div className="org-dialog-loader">
              <ClipLoader color="rgba(0, 177, 183, 1)" size={32} />
            </div>
          ) : (
            <>
              <div className="middle-section org-dialog-fields">
                <div className="org-dialog-row">
                  <InputComponent
                    classNameWrapper="w-100"
                    id="org-full-name-uk"
                    name="full_name_uk"
                    handleInput={isView ? undefined : setFullNameUk}
                    value={fullNameUk}
                    label="Full name"
                    disabled={isView}
                  />
                </div>
                <div className="org-dialog-row">
                  <InputComponent
                    classNameWrapper="org-dialog-half"
                    id="org-name"
                    name="name"
                    handleInput={isView ? undefined : setName}
                    value={name}
                    label="Short name (for select)"
                    disabled={isView}
                  />
                  <InputComponent
                    classNameWrapper="org-dialog-half"
                    id="org-abbreviation"
                    name="abbreviation"
                    handleInput={isView ? undefined : setAbbreviation}
                    value={abbreviation}
                    label="IMSMA abbreviation"
                    disabled={isView}
                  />
                </div>
                <div className="org-dialog-row">
                  <InputComponent
                    classNameWrapper="org-dialog-half"
                    id="org-imsma-username"
                    name="imsma_username"
                    handleInput={isView ? undefined : setImsmaUsername}
                    value={imsmaUsername}
                    label="IMSMA login"
                    disabled={isView}
                  />
                  {!isView && (
                    <InputComponent
                      classNameWrapper="org-dialog-half"
                      id="org-imsma-password"
                      name="imsma_password"
                      type="password"
                      handleInput={setImsmaPassword}
                      value={imsmaPassword}
                      label={isEdit ? 'IMSMA password (leave blank to keep current)' : 'IMSMA password'}
                    />
                  )}
                </div>





                <div className="org-dialog-layers-title">IMSMA layers</div>
                {REPORT_TYPES.map((type) => (
                  <div key={type} className="org-dialog-layer-group">
                    <div className="org-dialog-layer-label">{LAYER_LABELS[type]}</div>
                    <div className="org-dialog-row">
                      <InputComponent
                        classNameWrapper="org-dialog-half"
                        id={`org-${type}-validated`}
                        name={`${type}_validated`}
                        handleInput={isView ? undefined : (val) => updateLayer(type, 'validated_application_id', val)}
                        value={layers[type].validated_application_id}
                        label="Validated Application ID"
                        disabled={isView}
                      />
                      <InputComponent
                        classNameWrapper="org-dialog-half"
                        id={`org-${type}-not-validated`}
                        name={`${type}_not_validated`}
                        handleInput={isView ? undefined : (val) => updateLayer(type, 'not_validated_application_id', val)}
                        value={layers[type].not_validated_application_id}
                        label="Not Validated Application ID"
                        disabled={isView}
                      />
                    </div>
                  </div>
                ))}
                <div className="org-dialog-status-row">
                  <SwitherComponent
                    checked={isActive}
                    handleSwitcher={isView ? () => {} : setIsActive}
                    labelLeft="Inactive"
                    labelRight="Active"
                  />
                </div>
                <div className="select-wrapper w-100" style={{ gap: '0', width: '100%' }}>
                  <div className="label">
                    User
                  </div>
                  <SelectComponent
                    id="org-users"
                    name="org-users"
                    label="Users"
                    isMulti
                    isDisabled={isView}
                    value={selectedUsers}
                    options={convertObjectToArrayUsers(allUsers)}
                    handleSelect={(values) => setSelectedUsers(values || [])}
                    className="full"
                    menuPortalTarget={document.body}
                    customStyles={{
                      menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                      menu: (base) => ({ ...base, zIndex: 2000 }),
                    }}
                  />
                </div>
              </div>

              {!isView && (
                <div className="buttons-group">
                  <ButtonComponent
                    iconLeft={<CancelSvg />}
                    className="danger"
                    handleButton={handleClose}
                    label="Cancel"
                  />
                  <ButtonComponent
                    iconLeft={loader ? (
                      <ClipLoader color="rgba(0, 177, 183, 1)" size={20} />
                    ) : isCreate ? <AddSvg /> : <SendSvg />}
                    handleButton={handleSave}
                    label={isCreate ? 'Create' : 'Save'}
                    disabled={loader}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </Dialog>
    </div>
  );
};
